import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import {
  Eye,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Target,
  Shield,
  Sparkles,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  const map = {
    good: "bg-emerald-100 text-emerald-700",
    warning: "bg-amber-100 text-amber-700",
    danger: "bg-red-100 text-red-700",
    neutral: "bg-slate-100 text-slate-600",
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${map[status] || map.neutral}`}>{status}</span>;
}

function TrendIcon({ trend }) {
  if (trend === "up") return <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />;
  if (trend === "down") return <TrendingDown className="w-3.5 h-3.5 text-red-500" />;
  return <Minus className="w-3.5 h-3.5 text-slate-400" />;
}

function SeverityBadge({ severity }) {
  const map = {
    critical: "bg-red-600 text-white",
    high: "bg-red-100 text-red-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-sky-100 text-sky-700",
    good: "bg-emerald-100 text-emerald-700",
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${map[severity] || map.medium}`}>{severity}</span>;
}

function PriorityIcon({ priority }) {
  if (priority === "critical") return <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />;
  if (priority === "high") return <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />;
  return <Shield className="w-4 h-4 text-sky-600 shrink-0" />;
}

export default function ReportInsightsViewer({ reportId, reportTitle, open, onClose }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [source, setSource] = useState("pre_generated");

  useEffect(() => {
    if (open && reportId) fetchInsights();
  }, [open, reportId]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/api/report-insights/${reportId}`);
      if (res.data.success) {
        setData(res.data.data);
        setSource(res.data.source);
      }
    } catch (err) {
      toast.error("Failed to load report insights");
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshAI = async () => {
    setRefreshing(true);
    toast.info("Enhancing insights with AI... This may take up to 60 seconds.");
    try {
      const res = await api.post(`${API}/api/report-insights/refresh-ai`, {
        report_id: reportId,
        sub_industry: "insurance",
      });
      if (res.data.success) {
        setData(res.data.data);
        setSource("ai_enhanced");
        toast.success("Insights enhanced with AI analysis");
      } else {
        toast.error(res.data.error || "AI enhancement failed");
      }
    } catch {
      toast.error("AI enhancement failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="report-insights-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-base">{reportTitle}</DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-[10px]">Insurance Industry</Badge>
                {source === "ai_enhanced" ? (
                  <Badge className="bg-violet-100 text-violet-700 text-[10px]">
                    <Sparkles className="w-2.5 h-2.5 mr-1" /> AI-Enhanced
                  </Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 text-[10px]">Pre-Generated</Badge>
                )}
              </div>
            </div>
            <Button
              size="sm"
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-xs h-8"
              onClick={handleRefreshAI}
              disabled={refreshing}
              data-testid="refresh-ai-insights-btn"
            >
              {refreshing ? (
                <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Enhancing...</>
              ) : (
                <><Sparkles className="w-3 h-3 mr-1.5" /> Refresh with AI</>
              )}
            </Button>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="insights-loading">
            <Loader2 className="w-6 h-6 animate-spin text-sky-600 mr-2" />
            <span className="text-sm text-slate-500">Loading insights...</span>
          </div>
        ) : data ? (
          <div className="space-y-6 mt-2">
            {/* KPIs Section */}
            <div data-testid="kpi-section">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4 text-sky-600" /> Key Performance Indicators (KPIs)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(data.kpis || []).map((kpi, idx) => (
                  <div
                    key={idx}
                    className="border rounded-lg p-3 hover:border-sky-200 transition-colors"
                    data-testid={`kpi-card-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-slate-600">{kpi.name}</span>
                      <div className="flex items-center gap-1.5">
                        <StatusBadge status={kpi.status} />
                        <TrendIcon trend={kpi.trend} />
                      </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xl font-bold text-slate-900">{kpi.value}</span>
                      <span className="text-[10px] text-slate-400">Target: {kpi.target}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">{kpi.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* KRIs Section */}
            <div data-testid="kri-section">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-600" /> Key Risk Indicators (KRIs)
              </h3>
              <div className="space-y-2">
                {(data.kris || []).map((kri, idx) => (
                  <div
                    key={idx}
                    className={`border rounded-lg p-3 ${
                      kri.severity === "critical" ? "border-red-200 bg-red-50/50" :
                      kri.severity === "high" ? "border-amber-200 bg-amber-50/30" : "bg-white"
                    }`}
                    data-testid={`kri-card-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <SeverityBadge severity={kri.severity} />
                        <span className="text-sm font-medium text-slate-800">{kri.name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-bold text-slate-900">{kri.value}</span>
                        <span className="text-slate-400">Threshold: {kri.threshold}</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1.5 pl-[52px] leading-relaxed">{kri.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Attention Items Section */}
            <div data-testid="attention-section">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" /> Items Requiring Attention
              </h3>
              <div className="space-y-3">
                {(data.attention_items || []).map((item, idx) => (
                  <div
                    key={idx}
                    className={`border rounded-lg overflow-hidden ${
                      item.priority === "critical" ? "border-red-300" :
                      item.priority === "high" ? "border-amber-300" : "border-slate-200"
                    }`}
                    data-testid={`attention-item-${idx}`}
                  >
                    <div className={`px-4 py-2.5 flex items-center gap-2 ${
                      item.priority === "critical" ? "bg-red-50" :
                      item.priority === "high" ? "bg-amber-50" : "bg-slate-50"
                    }`}>
                      <PriorityIcon priority={item.priority} />
                      <span className="text-sm font-semibold text-slate-800 flex-1">{item.title}</span>
                      <Badge variant="outline" className={`text-[10px] ${
                        item.priority === "critical" ? "border-red-300 text-red-700" :
                        item.priority === "high" ? "border-amber-300 text-amber-700" :
                        "border-slate-300 text-slate-600"
                      }`}>
                        {item.priority}
                      </Badge>
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-xs text-slate-600 leading-relaxed">{item.detail}</p>
                      {item.recommendation && (
                        <div className="mt-2 p-2 bg-emerald-50 rounded border border-emerald-100">
                          <p className="text-[11px] text-emerald-800">
                            <CheckCircle2 className="w-3 h-3 inline mr-1" />
                            <strong>Recommendation:</strong> {item.recommendation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="pt-2 border-t text-[10px] text-slate-400 flex items-center justify-between">
              <span>Generated: {data.generated_at ? new Date(data.generated_at).toLocaleString() : "N/A"}</span>
              <span>Source: {source === "ai_enhanced" ? "AI-Enhanced" : "Pre-Generated Intelligence"}</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500 text-sm">No insights available for this report.</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
